import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import {
  safeAddBodyClass,
  safeRemoveBodyClass,
  safeSetDocumentElementAttribute,
} from './domQuery';
import { refreshMobileThemeColor } from './pwaInstall';

export const COLOR_SCHEMES = ['light', 'dark', 'auto'];

export const THEME_PALETTES = [
  {
    key: 'otello',
    name: 'Otello',
    mode: 'light',
    description: 'Chiaro bianco, familiare, con accento brand.',
    accent: '#2563EB',
    accentForeground: '#FFFFFF',
    accentText: '#1D4ED8',
    accentStrong: '#1D4ED8',
    tokens: {
      bg: '#FFFFFF',
      sidebar: '#F8FAFC',
      panel: '#FFFFFF',
      panelSoft: '#F4F7FC',
      panelRaised: '#FFFFFF',
      hover: '#F2F7FF',
      active: '#EAF2FF',
      selected: '#DDEAFF',
      border: '#DCE3EF',
      borderStrong: '#AFC0D8',
      text: '#172033',
      muted: '#667085',
      subtle: '#98A2B3',
      input: '#F3F6FA',
      inputFocus: '#FFFFFF',
      composer: '#FFFFFF',
      statusError: '#B42318',
      statusErrorBg: '#FEF3F2',
      statusSuccess: '#067647',
      statusSuccessBg: '#ECFDF3',
      statusWarning: '#854D0E',
      statusWarningBg: '#FFFBEB',
      shadow: 'rgba(15, 23, 42, 0.12)',
      specialIconFilter:
        'brightness(0) saturate(100%) invert(38%) sepia(90%) saturate(2170%) hue-rotate(222deg) brightness(101%) contrast(98%)',
    },
  },
  {
    key: 'carbon',
    name: 'Carbon',
    mode: 'dark',
    description: 'Scuro neutro, denso e senza dominante cromatica.',
    accent: '#5EA0EF',
    accentForeground: '#111111',
    accentText: '#9BCBFF',
    accentStrong: '#9BCBFF',
    tokens: {
      bg: '#111111',
      sidebar: '#181818',
      panel: '#1F1F1F',
      panelSoft: '#252526',
      panelRaised: '#161616',
      hover: '#2A2A2A',
      active: '#333333',
      selected: '#3A3A3A',
      border: '#2F2F2F',
      borderStrong: '#4B4B4B',
      text: '#E6E6E6',
      muted: '#A1A1AA',
      subtle: '#737373',
      input: '#1A1A1A',
      inputFocus: '#222222',
      composer: '#202020',
      statusError: '#FDA29B',
      statusErrorBg: '#3B1C1C',
      statusSuccess: '#75E0A7',
      statusSuccessBg: '#123524',
      statusWarning: '#FDE68A',
      statusWarningBg: '#3A2C12',
      shadow: 'rgba(0, 0, 0, 0.42)',
      specialIconFilter:
        'brightness(0) saturate(100%) invert(69%) sepia(36%) saturate(1380%) hue-rotate(183deg) brightness(98%) contrast(91%)',
    },
  },
  {
    key: 'paper',
    name: 'Paper',
    mode: 'light',
    description: 'Chiaro neutro e basso contrasto, da lavoro prolungato.',
    accent: '#006AB1',
    accentForeground: '#FFFFFF',
    accentText: '#005A9C',
    accentStrong: '#3B82C4',
    tokens: {
      bg: '#F7F7F5',
      sidebar: '#F0F0EC',
      panel: '#FFFFFF',
      panelSoft: '#EBECE7',
      panelRaised: '#FFFFFF',
      hover: '#EEEFEA',
      active: '#E2E6EA',
      selected: '#D8E5F2',
      border: '#D4D7D3',
      borderStrong: '#B8BDB7',
      text: '#2D2D2D',
      muted: '#6D6D6D',
      subtle: '#929292',
      input: '#EDEDE8',
      inputFocus: '#FFFFFF',
      composer: '#F3F3EF',
      statusError: '#B42318',
      statusErrorBg: '#FEF3F2',
      statusSuccess: '#067647',
      statusSuccessBg: '#ECFDF3',
      statusWarning: '#854D0E',
      statusWarningBg: '#FFFBEB',
      shadow: 'rgba(39, 39, 42, 0.11)',
      specialIconFilter:
        'brightness(0) saturate(100%) invert(31%) sepia(93%) saturate(1211%) hue-rotate(179deg) brightness(92%) contrast(101%)',
    },
  },
  {
    key: 'lumen',
    name: 'Lumen',
    mode: 'light',
    description: 'Freddo chiaro, calmo, con contrasto leggero.',
    accent: '#7C70A8',
    accentForeground: '#000000',
    accentText: '#5F4B8B',
    accentStrong: '#5BA7A4',
    tokens: {
      bg: '#F7F6FB',
      sidebar: '#EEF1F6',
      panel: '#FFFFFF',
      panelSoft: '#EEF0F7',
      panelRaised: '#FAFBFF',
      hover: '#E9EDF7',
      active: '#E2E7F3',
      selected: '#DCEAF0',
      border: '#D5DAE6',
      borderStrong: '#B9C3D3',
      text: '#202636',
      muted: '#667085',
      subtle: '#95A0AF',
      input: '#EEF1F7',
      inputFocus: '#FFFFFF',
      composer: '#F0F3F8',
      statusError: '#B42318',
      statusErrorBg: '#FEF3F2',
      statusSuccess: '#067647',
      statusSuccessBg: '#ECFDF3',
      statusWarning: '#854D0E',
      statusWarningBg: '#FFFBEB',
      shadow: 'rgba(40, 47, 68, 0.11)',
      specialIconFilter:
        'brightness(0) saturate(100%) invert(48%) sepia(14%) saturate(1088%) hue-rotate(214deg) brightness(91%) contrast(90%)',
    },
  },
  {
    key: 'lagoon',
    name: 'Lagoon',
    mode: 'dark',
    description: 'Petrolio scuro con testo caldo e bordi morbidi.',
    accent: '#2AA198',
    accentForeground: '#000000',
    accentText: '#7BE0D5',
    accentStrong: '#268BD2',
    tokens: {
      bg: '#002B36',
      sidebar: '#073642',
      panel: '#0A3A46',
      panelSoft: '#123F4C',
      panelRaised: '#06313C',
      hover: '#164955',
      active: '#1D5661',
      selected: '#245F68',
      border: '#24545D',
      borderStrong: '#396A72',
      text: '#EEE8D5',
      muted: '#93A1A1',
      subtle: '#657B83',
      input: '#073642',
      inputFocus: '#0B3A46',
      composer: '#103946',
      statusError: '#FDA29B',
      statusErrorBg: '#3B1C1C',
      statusSuccess: '#75E0A7',
      statusSuccessBg: '#123524',
      statusWarning: '#FDE68A',
      statusWarningBg: '#3A2C12',
      shadow: 'rgba(0, 0, 0, 0.4)',
      specialIconFilter:
        'brightness(0) saturate(100%) invert(56%) sepia(62%) saturate(391%) hue-rotate(127deg) brightness(91%) contrast(90%)',
    },
  },
  {
    key: 'graphite',
    name: 'Graphite',
    mode: 'dark',
    description: 'Scuro neutro professionale, quasi monocromatico.',
    accent: '#B4C0D1',
    accentForeground: '#111315',
    accentText: '#B4C0D1',
    accentStrong: '#5F6F86',
    tokens: {
      bg: '#111315',
      sidebar: '#151719',
      panel: '#1A1D21',
      panelSoft: '#20242A',
      panelRaised: '#15181C',
      hover: '#272C34',
      active: '#303744',
      selected: '#3B4351',
      border: '#343A44',
      borderStrong: '#46505E',
      text: '#EDF0F4',
      muted: '#AAB2BD',
      subtle: '#7B8490',
      input: '#181B20',
      inputFocus: '#20242A',
      composer: '#20242B',
      statusError: '#FDA29B',
      statusErrorBg: '#3B1C1C',
      statusSuccess: '#75E0A7',
      statusSuccessBg: '#123524',
      statusWarning: '#FDE68A',
      statusWarningBg: '#3A2C12',
      shadow: 'rgba(0, 0, 0, 0.35)',
      specialIconFilter:
        'brightness(0) saturate(100%) invert(80%) sepia(9%) saturate(414%) hue-rotate(177deg) brightness(97%) contrast(89%)',
    },
  },
];

export const DEFAULT_COLOR_SCHEME = 'auto';
export const DEFAULT_THEME_PALETTE = 'otello';
export const DEFAULT_THEME_PALETTE_BY_MODE = {
  dark: 'carbon',
  light: 'otello',
};
export const SYSTEM_THEME_PALETTE = 'system';

export const withoutThemePreferenceKeys = (uiSettings = {}) => {
  const nextUISettings = { ...(uiSettings || {}) };
  delete nextUISettings[LOCAL_STORAGE_KEYS.COLOR_SCHEME];
  delete nextUISettings[LOCAL_STORAGE_KEYS.THEME_PALETTE];
  return nextUISettings;
};

export const clearLocalThemePreferences = () => {
  LocalStorage.remove(LOCAL_STORAGE_KEYS.COLOR_SCHEME);
  LocalStorage.remove(LOCAL_STORAGE_KEYS.THEME_PALETTE);
};

const LEGACY_THEME_KEYS = {
  cursor_dark: 'carbon',
  midnight: 'graphite',
  quiet_light: 'paper',
  solarized_light: 'paper',
  solarized_dark: 'lagoon',
  nightfall: 'graphite',
  monokai: 'graphite',
  volt: 'graphite',
  tomorrow_blue: 'lagoon',
  harbor: 'lagoon',
  ocean: 'lagoon',
  pelago: 'lagoon',
  sandbar: 'paper',
  ruby: 'carbon',
  ember: 'carbon',
  forest: 'otello',
  signal: 'otello',
};

const normalizeThemePalette = (palette) =>
  LEGACY_THEME_KEYS[palette] || palette;

const getThemePreference = (key, uiSettings = null) => {
  if (uiSettings) {
    return Object.prototype.hasOwnProperty.call(uiSettings, key)
      ? uiSettings[key]
      : null;
  }

  return LocalStorage.get(key);
};

export const getStoredColorScheme = (uiSettings = null) => {
  const storedColorScheme = getThemePreference(
    LOCAL_STORAGE_KEYS.COLOR_SCHEME,
    uiSettings
  );
  if (COLOR_SCHEMES.includes(storedColorScheme)) {
    return storedColorScheme;
  }

  const storedPalette = normalizeThemePalette(
    getThemePreference(LOCAL_STORAGE_KEYS.THEME_PALETTE, uiSettings)
  );
  const storedTheme = THEME_PALETTES.find(
    (theme) => theme.key === storedPalette
  );

  return storedTheme?.mode || DEFAULT_COLOR_SCHEME;
};

export const getStoredThemePalette = (uiSettings = null) => {
  const storedPalette = normalizeThemePalette(
    getThemePreference(LOCAL_STORAGE_KEYS.THEME_PALETTE, uiSettings)
  );
  const storedTheme = THEME_PALETTES.find(
    (theme) => theme.key === storedPalette
  );
  const storedColorScheme = getStoredColorScheme(uiSettings);

  if (
    ['dark', 'light'].includes(storedColorScheme) &&
    (!storedTheme || storedTheme.mode !== storedColorScheme)
  ) {
    return DEFAULT_THEME_PALETTE_BY_MODE[storedColorScheme];
  }

  return storedTheme?.key || DEFAULT_THEME_PALETTE;
};

export const getStoredThemePaletteSelection = (uiSettings = null) =>
  getStoredColorScheme(uiSettings) === 'auto'
    ? SYSTEM_THEME_PALETTE
    : getStoredThemePalette(uiSettings);

const removeThemePaletteClasses = () => {
  THEME_PALETTES.forEach((theme) => safeRemoveBodyClass(`theme-${theme.key}`));
  Object.keys(LEGACY_THEME_KEYS).forEach((themeKey) =>
    safeRemoveBodyClass(`theme-${themeKey}`)
  );
};

const getThemePalette = (palette = getStoredThemePalette()) =>
  THEME_PALETTES.find(
    (theme) => theme.key === normalizeThemePalette(palette)
  ) || THEME_PALETTES.find((theme) => theme.key === DEFAULT_THEME_PALETTE);

const getThemePaletteForMode = (colorMode, uiSettings = null) => {
  if (getStoredColorScheme(uiSettings) === 'auto') {
    return getThemePalette(DEFAULT_THEME_PALETTE_BY_MODE[colorMode]);
  }

  const storedTheme = getThemePalette(getStoredThemePalette(uiSettings));
  if (storedTheme?.mode === colorMode) {
    return storedTheme;
  }
  return getThemePalette(DEFAULT_THEME_PALETTE_BY_MODE[colorMode]);
};

const setBodyThemeVariable = (name, value) => {
  if (typeof document === 'undefined' || !document.body?.style) {
    return;
  }

  document.body.style.setProperty(name, value);
};

const applyThemeVariables = (theme) => {
  const modeTokens = theme?.tokens || {};

  const variables = {
    '--theme-accent': theme?.accent,
    '--theme-accent-foreground': theme?.accentForeground,
    '--theme-accent-text': theme?.accentText || theme?.accent,
    '--theme-accent-strong': theme?.accentStrong || theme?.accent,
    '--theme-bg': modeTokens.bg,
    '--theme-sidebar-bg': modeTokens.sidebar,
    '--theme-panel': modeTokens.panel,
    '--theme-panel-soft': modeTokens.panelSoft,
    '--theme-panel-raised': modeTokens.panelRaised,
    '--theme-hover': modeTokens.hover,
    '--theme-active': modeTokens.active,
    '--theme-selected': modeTokens.selected,
    '--theme-border': modeTokens.border,
    '--theme-border-strong': modeTokens.borderStrong,
    '--theme-text': modeTokens.text,
    '--theme-muted': modeTokens.muted,
    '--theme-subtle': modeTokens.subtle,
    '--theme-input': modeTokens.input,
    '--theme-input-focus': modeTokens.inputFocus,
    '--theme-composer': modeTokens.composer,
    '--theme-status-error': modeTokens.statusError,
    '--theme-status-error-bg': modeTokens.statusErrorBg,
    '--theme-status-success': modeTokens.statusSuccess,
    '--theme-status-success-bg': modeTokens.statusSuccessBg,
    '--theme-status-warning': modeTokens.statusWarning,
    '--theme-status-warning-bg': modeTokens.statusWarningBg,
    '--theme-shadow': modeTokens.shadow,
    '--theme-special-icon-filter': modeTokens.specialIconFilter,
  };

  Object.entries(variables).forEach(([name, value]) => {
    if (value) {
      setBodyThemeVariable(name, value);
    }
  });
};

export const applyThemePalette = (
  palette = getStoredThemePalette(),
  colorMode = null
) => {
  const selectedTheme = getThemePalette(palette);
  const resolvedTheme =
    colorMode && selectedTheme?.mode !== colorMode
      ? getThemePalette(DEFAULT_THEME_PALETTE_BY_MODE[colorMode])
      : selectedTheme;
  const selectedPalette = resolvedTheme?.key || DEFAULT_THEME_PALETTE;

  removeThemePaletteClasses();
  safeAddBodyClass(`theme-${selectedPalette}`);
  applyThemeVariables(resolvedTheme);
  return resolvedTheme;
};

export const resolveColorMode = (isOSOnDarkMode, uiSettings = null) => {
  const selectedColorScheme = getStoredColorScheme(uiSettings);
  if (
    (selectedColorScheme === 'auto' && isOSOnDarkMode) ||
    selectedColorScheme === 'dark'
  ) {
    return 'dark';
  }
  return 'light';
};

export const setColorTheme = (isOSOnDarkMode, uiSettings = null) => {
  const colorMode = resolveColorMode(isOSOnDarkMode, uiSettings);
  const selectedTheme = getThemePaletteForMode(colorMode, uiSettings);
  const selectedColorMode = selectedTheme?.mode || colorMode;

  if (selectedColorMode === 'dark') {
    safeAddBodyClass('dark');
    safeSetDocumentElementAttribute('style', 'color-scheme: dark;');
  } else {
    safeRemoveBodyClass('dark');
    safeSetDocumentElementAttribute('style', 'color-scheme: light;');
  }

  applyThemePalette(selectedTheme?.key, selectedColorMode);
  // Otello da mobile: barra di stato del colore del tema.
  refreshMobileThemeColor();
};
